/**
 * sadePanel.js — the company portal's SIMPLE panel (backlog #704, the frontend half of FDS-Backend #703, 2026-11-03).
 *
 * The simple panel is the layout the Yabancı Katılımcı set has had since #511 / #527 / #535 / #607: fewer menu items and Home widgets,
 * no Widget Ekle, no Mobil Uygulama, no identity card on Home, the short personnel list (three tabs, the whiteboard row card). Since #703
 * staff can give it to EVERY company login with one switch, Sistem Ayarları › Kayıt Ayarları › Sade panel modu (`portal.sade_panel`, OFF by
 * default): the backend then holds every set to the Yabancı Katılımcı module caps and GET /portal/context answers `sadePanel: true`. The set
 * code stays the company's own (a Yerli Dekoratör is still YERLI_DEKORATOR), so the layout may not be keyed on the code alone.
 *
 * Two questions, kept apart on purpose (Deniz 2026-11-03: the Yerli / Yabancı distinction of company AND kişi documents stays intact):
 *   · sadePanelMi(portal)  — is the simple LAYOUT shown. The real Yabancı Katılımcı set, or any company login while the switch is ON.
 *   · yabanciKatilimciMi   — (Components/Kisi/YabanciKatilimciParcalari) does the login get the Yabancı Katılımcı's own DOCUMENT lists
 *                            (company: the İSG Taahhütnamesi, İmza Sirküsü optional since #742, no OHS officers; kişi: GDPR + passport). Only the real set: every
 *                            other company keeps the lists the backend sends for ITS nationality and role, switch or no switch.
 * Companies are told nothing about the switch (Deniz 2026-11-03): no banner, no text. WAS (#704 as written): "a banner [yes, TR + EN]".
 * Pure, so jest can try it (sadePanel.test.js).
 */

/** The backend's interface-set code of a foreign KATILIMCI (PortalConstant); YabanciKatilimciParcalari.YABANCI_KATILIMCI_KODU is the same. */
export const YABANCI_KATILIMCI_SET_KODU = "YABANCI_KATILIMCI";

/** Is the simple panel's LAYOUT shown: the active set is the Yabancı Katılımcı, or Sade panel modu is ON (`portal.sadePanel`). */
export const sadePanelMi = (portal) => portal?.aktif?.kod === YABANCI_KATILIMCI_SET_KODU || portal?.sadePanel === true;

/**
 * The set code the LAYOUT tables are keyed on (setMenusu.SET_GIZLI_MENU_OGELERI, anasayfaModulleri.SET_WIDGETLARI / _YUKLEME_SATIRI_*,
 * anasayfaWidgetDurumu.SET_VARSAYILAN_DUZEN): the Yabancı Katılımcı's while the simple panel is shown, else the active set's own (null
 * without one). A layout saved under the simple panel's key therefore never overwrites the company's own, and OFF brings that back.
 * Never for documents: those read the real set (yabanciKatilimciMi) or the backend's lists.
 */
export const gorunumSetKodu = (portal) => (sadePanelMi(portal) ? YABANCI_KATILIMCI_SET_KODU : portal?.aktif?.kod ?? null);

/**
 * The backend's portal refusals that say a MODULE (or an organizer area / action) is closed: AbstractController.refuseUnlessModul,
 * PortalService and the organizer guards (FDS-Backend messages_*.properties). The context is re-read on one, because the switch may
 * have changed since it was read (PortalContext only refetches on token, fair or language otherwise).
 */
export const PORTAL_RED_ANAHTARLARI = new Set([
  "yetki.firma_rolu_izin_vermiyor",
  "yetki.portal_rolu_izin_vermiyor",
  "yetki.modul_kisitli",
  "yetki.modul_kisitli_nedenli",
  "portal.alan_gorunumu_yok",
  "portal.fuar_gorunumu_yok",
  "portal.eylem_ayar_kapali",
  "portal.eylem_rol_yok",
  "portal.eylem_modul_kisitli",
  "portal.eylem_yetki_yok",
  "stand.rol_talebe_izin_vermiyor",
]);

// "(PROJELER, WRITE)": every module refusal names the module and the level it needed, whatever its key.
const MODUL_SEVIYESI = /\([A-Z_]+, (?:NONE|READ|WRITE|MANAGE)\)/;

/** Is this axios error a 403 that names a portal module (by its messageKey, or by "(MODUL, SEVIYE)" in the text)? */
export function modulReddiMi(error) {
  const cevap = error?.response;
  if (!cevap || cevap.status !== 403) return false;
  const govde = cevap.data && typeof cevap.data === "object" ? cevap.data : {};
  if (typeof govde.messageKey === "string" && PORTAL_RED_ANAHTARLARI.has(govde.messageKey)) return true;
  return typeof govde.message === "string" && MODUL_SEVIYESI.test(govde.message);
}

/** How often an open company portal re-reads its context in the background, so a switch flipped by staff reaches it without a reload. */
export const BAGLAM_YOKLAMA_MS = 5 * 60 * 1000;

/** A burst of module refusals (a page firing several reads) re-reads the context once in this window. */
export const RED_YENILEME_ARALIGI_MS = 15 * 1000;
